// Shared with mju-ai-workbench/shared/schemas/activityGraph.ts — keep the two identical but for this header.
// The workbench builds the graph itself for its own history; AIOE builds the same shape for an organisation
// project from what the project's workbenches uploaded (packages/api/src/history/). Members of the project
// (a sign-in or a device token) read:
//
//   GET /projects/v1/:id/history/graph?scope=run|agent|task|conversation&id=<scope id>&team=&from=&to=&types=&search=
//        -> ActivityGraph. `id`: the run's id; the agent's name; the task's id (or `<board>/<taskId>`); the
//        Team Work conversation's id. `team` narrows an agent or names a task's board. `from` and `to` (ISO)
//        cut the window (an agent: the last 30 days; a run, task or conversation: its own span).
//        `types` (comma-separated node kinds below) drops nodes of other kinds and their edges; `search` (case-insensitive
//        substring of a node's label or detail, at most 200 characters) keeps the matching nodes and their direct
//        neighbours; both apply before ACTIVITY_MAX_NODES, `types` first. The graph's shape does not change.
//        400 invalid_request for a bad scope, time or kind in `types`; 404 for a run, task or conversation the project does not hold.
//   GET /projects/v1/:id/history/runs?from=&to=&limit=&agent=&team=
//        -> HistoryRunsResponse (./historyRuns.ts): the project's runs for the timeline, with counts.
//
// Console link to one run's history: /projects/<id>?tab=overview&part=history&run=<runId>
/**
 * The activity graph (roadmap D240): one run, one agent's history, one task or
 * one conversation drawn as a graph of what happened — the agent sessions, the
 * tools they called, the commands they ran, the files they read and changed,
 * the connectors they reached, the questions they asked, the approvals held,
 * the context resets, the hand-overs and the review verdicts.
 *
 * The workbench builds it on the host from its session history, orchestration
 * log, audit log, usage ledger and run record; the organisation's console
 * builds the same shape for an organisation project from the Team Work
 * history the workbenches upload and its own audit log. So nothing here is
 * machine-local: ids, names, repository-relative paths, tool names, shortened
 * and redacted command lines, counts and times (ISO). Never prompt text, file
 * contents or secrets.
 *
 * Words for people: activity, tools, commands, files read, files changed,
 * connectors, questions, approvals, context reset, hand-over, verdict.
 */
import { z } from "zod";
/** What a node is. The renderer colours and shapes by this; a legend names each. */
export const ACTIVITY_NODE_KINDS = [
    "run",
    "agent",
    "tool",
    /** One of Microsoft Foundry's own tools (D273): it ran inside Foundry, allowed by the agent definition, not the gate. */
    "foundry-tool",
    "command",
    "file-read",
    "file-changed",
    "connector",
    "question",
    "approval",
    "reset",
    "handover",
    "verdict",
];
export const activityNodeKindSchema = z.enum(ACTIVITY_NODE_KINDS);
/** The most occurrence times one node carries (the scrubber's ticks); the earliest and latest are always kept. */
export const ACTIVITY_MAX_TIMES = 40;
/** The most nodes in one graph; past it the least-used leaves fold away and `truncated` says so. */
export const ACTIVITY_MAX_NODES = 400;
const iso = z.string().min(1).max(40);
export const activityNodeSchema = z.object({
    /** Stable within one graph (`kind:key`). */
    id: z.string().min(1).max(300),
    kind: activityNodeKindSchema,
    /** What a person reads on the node: an agent's name, a tool's name, a shortened command line, a repository-relative path. */
    label: z.string().max(200),
    /** One more line for the detail pane: the tool's kind, a verdict's words, a question's status. */
    detail: z.string().max(300).optional(),
    /** How many times it happened (a tool's calls, a file's touches); 1 for the run and an agent. */
    count: z.number().int().min(0),
    /** Tokens used (the run, an agent session), when the usage ledger knows. */
    tokens: z.number().int().min(0).optional(),
    /** The agent it belongs to (for a leaf, the agent that touched it first). */
    agent: z.string().max(200).optional(),
    /** When it first and last happened. */
    firstAt: iso.optional(),
    lastAt: iso.optional(),
    /** Every time it happened, oldest first, at most ACTIVITY_MAX_TIMES. */
    times: z.array(iso).max(ACTIVITY_MAX_TIMES).optional(),
    /** Where the history of this moment is kept: a conversation, and the agent session in it. */
    conversationId: z.string().max(200).optional(),
    sessionId: z.string().max(200).optional(),
    /** A short state word: ok, failed, denied, answered, open, approved, changes requested. */
    status: z.string().max(60).optional(),
});
/**
 * `part`: the run → an agent session (the tree's spine). `sequence`: turn
 * order — one agent's session to the next, a hand-over to whom it handed to.
 * `touched`: an agent → what it called, ran, read, changed, asked or reached.
 */
export const activityEdgeSchema = z.object({
    from: z.string().min(1).max(300),
    to: z.string().min(1).max(300),
    kind: z.enum(["part", "sequence", "touched"]),
    /** For `touched`: how many times this agent touched it. */
    count: z.number().int().min(0).optional(),
});
/** What the graph is of. */
export const activityScopeSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("run"), runId: z.string().min(1).max(200) }),
    z.object({ kind: z.literal("agent"), agent: z.string().min(1).max(200), teamId: z.string().max(200).optional() }),
    z.object({ kind: z.literal("task"), teamId: z.string().min(1).max(200), taskId: z.string().min(1).max(200) }),
    z.object({ kind: z.literal("conversation"), conversationId: z.string().min(1).max(200) }),
]);
export const activityGraphSchema = z.object({
    scope: activityScopeSchema,
    /** The graph's title line: the run's title, the agent's name, the task's title, the conversation's title. */
    title: z.string().max(300),
    /** The time range it covers (ISO). */
    from: iso,
    to: iso,
    nodes: z.array(activityNodeSchema).max(ACTIVITY_MAX_NODES),
    edges: z.array(activityEdgeSchema).max(ACTIVITY_MAX_NODES * 4),
    /** Leaves were folded away to stay under ACTIVITY_MAX_NODES. */
    truncated: z.boolean().optional(),
    /** When it was built (ISO). */
    at: iso,
});
//# sourceMappingURL=activityGraph.js.map